/**
 * Custom hook for managing reporting dashboard URL state
 * Persists year, month, and trend window settings in the URL for shareability and bookmarking
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PeriodGrain } from '../utils/constants';

export interface ReportingUrlState {
  year: number;
  month: number;
  /** ISO week (1-53). Null means "last week of the selected month". */
  week: number | null;
  grain: PeriodGrain;
  trendWindow: number;
}

interface UseReportingUrlStateReturn {
  /** Current year from URL or default */
  year: number;
  /** Current month from URL or default */
  month: number;
  /** ISO week from URL, or null to use the last week of `month` */
  week: number | null;
  /** Week / month / quarter — drives the period picker label and step size */
  grain: PeriodGrain;
  /** Current trend window (3 or 6) from URL or default */
  trendWindow: number;
  /** Whether year/month were explicitly set in the URL (vs defaulting to current date) */
  hasExplicitPeriod: boolean;
  /** Whether an ISO week was explicitly set in the URL */
  hasExplicitWeek: boolean;
  /** Set the year in URL */
  setYear: (year: number) => void;
  /** Set the month in URL */
  setMonth: (month: number) => void;
  /** Set the ISO week in URL (null clears it) */
  setWeek: (week: number | null) => void;
  /** Set the trend window in URL */
  setTrendWindow: (trendWindow: number) => void;
  /** Set multiple values at once */
  setReportingState: (state: Partial<ReportingUrlState>) => void;
  /** Get search params string for navigation (preserves current settings) */
  getSearchParamsString: () => string;
  /** Build a full path with preserved search params */
  buildPath: (basePath: string) => string;
}

const PARAM_KEYS = {
  year: 'year',
  month: 'month',
  week: 'week',
  grain: 'grain',
  trend: 'trend'
} as const;

const DEFAULT_TREND_WINDOW = 6;

/**
 * Hook for managing reporting dashboard settings in URL search params
 *
 * This allows users to:
 * - Share dashboard links with specific date/trend settings
 * - Bookmark specific views
 * - Use browser back/forward to navigate between settings states
 */
export function useReportingUrlState(): UseReportingUrlStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current values from URL with defaults
  const currentState = useMemo(() => {
    const now = new Date();
    const defaultYear = now.getFullYear();
    const defaultMonth = now.getMonth() + 1;

    const yearParam = searchParams.get(PARAM_KEYS.year);
    const monthParam = searchParams.get(PARAM_KEYS.month);
    const weekParam = searchParams.get(PARAM_KEYS.week);
    const grainParam = searchParams.get(PARAM_KEYS.grain);
    const trendParam = searchParams.get(PARAM_KEYS.trend);

    // Parse and validate year
    const year = yearParam ? parseInt(yearParam, 10) : defaultYear;
    const validYear = !isNaN(year) && year >= 2000 && year <= 2100 ? year : defaultYear;

    // Parse and validate month (1-12)
    const month = monthParam ? parseInt(monthParam, 10) : defaultMonth;
    const validMonth = !isNaN(month) && month >= 1 && month <= 12 ? month : defaultMonth;

    const parsedWeek = weekParam ? parseInt(weekParam, 10) : NaN;
    const validWeek = !isNaN(parsedWeek) && parsedWeek >= 1 && parsedWeek <= 53 ? parsedWeek : null;

    const validGrain: PeriodGrain =
      grainParam === 'week' || grainParam === 'month' || grainParam === 'quarter'
        ? grainParam
        : validWeek !== null
          ? 'week'
          : 'month';

    // Parse and validate trend window (3 or 6)
    const trend = trendParam ? parseInt(trendParam, 10) : DEFAULT_TREND_WINDOW;
    const validTrend = trend === 3 || trend === 6 ? trend : DEFAULT_TREND_WINDOW;

    return {
      year: validYear,
      month: validMonth,
      week: validWeek,
      grain: validGrain,
      trendWindow: validTrend,
      hasExplicitPeriod: yearParam !== null || monthParam !== null || validWeek !== null,
      hasExplicitWeek: validWeek !== null
    };
  }, [searchParams]);

  // Helper to update URL params while preserving other params
  const updateParams = useCallback(
    (updates: Partial<Record<keyof typeof PARAM_KEYS, string | null>>) => {
      const newParams = new URLSearchParams(searchParams);
      const now = new Date();
      const defaultYear = now.getFullYear();
      const defaultMonth = now.getMonth() + 1;

      Object.entries(updates).forEach(([key, value]) => {
        const paramKey = PARAM_KEYS[key as keyof typeof PARAM_KEYS];
        if (paramKey) {
          // Only include non-default values to keep URLs clean
          const isDefault =
            (key === 'year' && value === String(defaultYear)) ||
            (key === 'month' && value === String(defaultMonth)) ||
            (key === 'week' && value === null) ||
            (key === 'grain' && value === 'month') ||
            (key === 'trend' && value === String(DEFAULT_TREND_WINDOW));

          if (value !== null && !isDefault) {
            newParams.set(paramKey, value);
          } else {
            newParams.delete(paramKey);
          }
        }
      });

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setYear = useCallback(
    (year: number) => {
      updateParams({ year: String(year) });
    },
    [updateParams]
  );

  const setMonth = useCallback(
    (month: number) => {
      updateParams({ month: String(month) });
    },
    [updateParams]
  );

  const setWeek = useCallback(
    (week: number | null) => {
      updateParams({ week: week === null ? null : String(week) });
    },
    [updateParams]
  );

  const setTrendWindow = useCallback(
    (trendWindow: number) => {
      updateParams({ trend: String(trendWindow) });
    },
    [updateParams]
  );

  const setReportingState = useCallback(
    (state: Partial<ReportingUrlState>) => {
      const updates: Partial<Record<keyof typeof PARAM_KEYS, string | null>> = {};
      if (state.year !== undefined) updates.year = String(state.year);
      if (state.month !== undefined) updates.month = String(state.month);
      if (state.week !== undefined) updates.week = state.week === null ? null : String(state.week);
      if (state.grain !== undefined) updates.grain = state.grain;
      if (state.trendWindow !== undefined) updates.trend = String(state.trendWindow);
      updateParams(updates);
    },
    [updateParams]
  );

  // Get search params string for navigation (only includes reporting params)
  const getSearchParamsString = useCallback(() => {
    const now = new Date();
    const defaultYear = now.getFullYear();
    const defaultMonth = now.getMonth() + 1;

    const params = new URLSearchParams();

    // Only include non-default values
    if (currentState.year !== defaultYear) {
      params.set(PARAM_KEYS.year, String(currentState.year));
    }
    if (currentState.month !== defaultMonth) {
      params.set(PARAM_KEYS.month, String(currentState.month));
    }
    if (currentState.week !== null) {
      params.set(PARAM_KEYS.week, String(currentState.week));
    }
    if (currentState.grain !== 'month') {
      params.set(PARAM_KEYS.grain, currentState.grain);
    }
    if (currentState.trendWindow !== DEFAULT_TREND_WINDOW) {
      params.set(PARAM_KEYS.trend, String(currentState.trendWindow));
    }

    const paramsString = params.toString();
    return paramsString ? `?${paramsString}` : '';
  }, [currentState]);

  // Build a full path with preserved search params
  const buildPath = useCallback(
    (basePath: string) => {
      return `${basePath}${getSearchParamsString()}`;
    },
    [getSearchParamsString]
  );

  return {
    year: currentState.year,
    month: currentState.month,
    week: currentState.week,
    grain: currentState.grain,
    trendWindow: currentState.trendWindow,
    hasExplicitPeriod: currentState.hasExplicitPeriod,
    hasExplicitWeek: currentState.hasExplicitWeek,
    setYear,
    setMonth,
    setWeek,
    setTrendWindow,
    setReportingState,
    getSearchParamsString,
    buildPath
  };
}
